// Wide screens (DESIGN.md Layout "Wide screens"; docs/design/SIVUPALSTA.md "Shell"): above 1920 px the
// whole PC composition scales with the screen — CSS `zoom` on <html> (styles/tokens.css, the "wide
// screen" block), so type, spacing, cards and controls grow together — and the fluid spread fills the
// zoomed page from the rail to the screen's right edge (nothing is centred; Axel 2026-10-09 "use full
// width on PC"). The CSS does the work; this module holds the same numbers for the tests
// (wideScale.test.ts checks the CSS against them) and the one helper that script measuring the page needs.
//
// The scale: 1 up to 1920 px (so 900–1920 px and every phone and tablet stay exactly as they were),
// linear to 1.3 at 2560 px, 1.3 above. It is computed from the width in CSS px, so a browser zoom
// (which narrows the CSS width) lowers it: the person's own zoom always wins.

export const WIDE_FROM = 1920; // px: the last width without scaling
export const WIDE_TO = 2560; // px: where the scale reaches its top
export const WIDE_MAX = 1.3;
export const WIDE_FRAME = 1920; // px (the page's own px): the narrowest page a wide screen's zoom leaves

/** The scale the CSS gives a viewport `width` CSS px wide. */
export function wideScale(width: number): number {
  if (!(width > WIDE_FROM)) return 1;
  return Math.min(WIDE_MAX, 1 + ((width - WIDE_FROM) * (WIDE_MAX - 1)) / (WIDE_TO - WIDE_FROM));
}

/**
 * The CSS zoom in effect on `el` (1 below 1921 px). Under a zoom, getBoundingClientRect() and pointer
 * coordinates are in screen px, while lengths written into a style (`top: …px`, a custom property) and
 * scrollTop are in the page's own px: divide a measured length by this before using it as one.
 */
export function cssZoom(el?: Element | null): number {
  if (typeof document === 'undefined') return 1;
  const node = el ?? document.documentElement;
  const current = (node as Element & { currentCSSZoom?: number }).currentCSSZoom;
  if (typeof current === 'number' && current > 0) return current;
  const z = Number.parseFloat(getComputedStyle(document.documentElement).zoom);
  return Number.isFinite(z) && z > 0 ? z : 1;
}
